<template>
  <main class="h-full">
    <section class="bg-h-primary-50 border py-2">
      <div class="container mx-auto">
        <BreadcrumbComponent :home="home" :items="items" />
      </div>
    </section>
    <section class="container mx-auto px-5 md:px-3 mt-10">
      <div class="grid grid-cols-12 items-start gap-3">
        <div class="col-span-12 md:col-span-3 flex flex-col gap-5 mt-6">
          <h4>
            {{ $t("labels.add", { pt: "Objetivo", en: "Objective" }) }}
          </h4>
          <p v-if="course.chapters" class="mt-[4px]">
            <b>
              {{ $t("labels.generic", { pt: "Capítulo: ", en: "Chapter: " }) }}
            </b>
            {{ chapter.name }}
          </p>
          <div
            class="rounded-lg border border-gray-200 overflow-hidden mt-[8px]"
          >
            <div class="flex flex-col w-100">
              <Button
                v-for="(item, i) in buttons"
                :key="i"
                :class="`${
                  i == active
                    ? 'btn-tab-active'
                    : item.done || !item.off
                    ? 'btn-tab-done'
                    : 'btn-tab-gray'
                }`"
                :disabled="item.off"
                @click="changeTab(item.active)"
              >
                <div class="flex justify-between items-center w-full">
                  <span class="hover:scale-95 transition-all">
                    {{ item.title }}
                  </span>
                  <v-icon
                    v-if="item.done"
                    name="hi-solid-check-circle"
                    class="text-green-500"
                  />
                  <v-icon
                    v-if="isEditPage && !item.done"
                    name="hi-solid-x-circle"
                    class="text-h-error-400"
                    :title="item.info"
                  />
                </div>
              </Button>
            </div>
          </div>
        </div>
        <div class="col-span-12 md:col-span-9">
          <TabView class="custom-tabs-objective" :activeIndex="active" lazy>
            <TabPanel>
              <FormInfo
                @onNext="changeTab(1)"
                @onAllow="allowSteps"
                :role="user.role"
              />
            </TabPanel>
            <TabPanel>
              <Materials
                @onNext="changeTab(2)"
                @onDeleted="(e) => isMaterialDeleted(e)"
                @onFilter="onFilter"
                @onSearch="onSearch"
                @onAllow="allowSteps"
                @onScroll="scrollMaterials"
                :role="user.role"
                :loader="loading"
              />
            </TabPanel>
            <TabPanel>
              <LessonPlanComponent
                @onNext="changeTab(3)"
                :lessonPlans="lessonPlans"
                :objective="objective"
                :loaderList="loading"
                :role="user.role"
              />
            </TabPanel>
            <TabPanel>
              <Quiz
                v-if="objective.id"
                @onAllow="allowSteps"
                :quizId="quizId"
                :data="quiz"
                :role="user.role"
                :objective="objective"
                :questions="questions"
                :course="course"
              />
            </TabPanel>
          </TabView>
        </div>
      </div>
    </section>
  </main>
</template>

<script lang="ts">
import { Chapter } from "@/models/chapter.model";
import { Course, CourseType } from "@/models/courses.model";
import { LessonPlan } from "@/models/lessonPlan.model";
import { Objective } from "@/models/objective.model";
import { QueryParams } from "@/models/query-params.model";
import { Question } from "@/models/quiz.model";
import { GoToRoute } from "@/utils/go-to.util";
import {
  removeItemLocalStorage,
  setItemLocalStorage,
} from "@/utils/save-storage.util";
import { getUserFromStorage } from "@/utils/user-store.util";
import { debounceTime, last, take } from "rxjs";
import { computed, defineAsyncComponent, defineComponent } from "vue";
import { CourseService } from "../Course/course.service";
import { InfoObjectiveService } from "./InfoGeneral/info.service";
import { LessonPlanService } from "./LessonPlan/lessonPlan.service";
import { MaterialsService } from "./Materials/materials.service";
import { ObjectiveService } from "./objective.service";
import { QuestionFactory } from "./Quiz/Questions/question.factory";
import { useQuizQuestionsDragNDropStore } from "@/store/quiz-question-drag";
import { useQuizQuestionsStore } from "@/store/quiz-questions";
import { restoreAnswerTextItem } from "./Quiz/Questions/question.utils";
import { useQuizQuestionsGapStore } from "@/store/quiz-question-gap";

const FormInfo = defineAsyncComponent(
  () => import("./InfoGeneral/form-info.vue")
);

const Materials = defineAsyncComponent(
  () => import("./Materials/materials.vue")
);

const LessonPlanComponent = defineAsyncComponent(
  () => import("./LessonPlan/lesson-plan.vue")
);

const Quiz = defineAsyncComponent(() => import("./Quiz/quiz.vue"));

export default defineComponent({
  name: "formTrail",
  components: { FormInfo, Materials, Quiz, LessonPlanComponent },
  computed: {
    serviceCourse(): CourseService {
      return new CourseService();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    serviceMaterial(): MaterialsService {
      return new MaterialsService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    serviceLessonPlan(): LessonPlanService {
      return new LessonPlanService();
    },
    user(): any {
      return getUserFromStorage();
    },
    isAdaptive(): boolean {
      return this.chapter?.trails === undefined;
    },
  },

  data() {
    return {
      home: { label: "", command: () => this.goBack() },
      items: [
        { label: this.$t("labels.add", { pt: "Objetivo", en: "Objective" }) },
      ],
      course: new Course(),
      chapter: new Chapter(),
      objective: new Objective(),
      quiz: {} as any,
      lessonPlans: [] as LessonPlan[],
      active: 0,
      buttons: [
        {
          title: this.$t("objectives.titles.info"),
          active: 0,
          off: false,
          done: null,
          info: "",
        },
        {
          title: this.$t("objectives.titles.materials"),
          active: 1,
          off: true,
          done: null,
          info: "",
        },
        {
          title: this.$t("objectives.titles.lessonPlans"),
          active: 2,
          off: true,
          done: null,
          info: "",
        },
        {
          title: this.$t("objectives.titles.quiz"),
          active: 3,
          off: true,
          done: null,
          info: "",
        },
      ] as any,
      steps: {
        allowMaterials: false,
        allowQuiz: false,
      } as any,
      objectiveId: "" as any,
      quizId: "" as any,
      params: new QueryParams(),
      loading: false as boolean,
      isEditPage: false as boolean,
      blockScroll: true as boolean,
      questions: [] as Question[],
      questionStore: QuestionFactory.selectStore(),
    };
  },
  mounted() {
    this.objectiveId = this.$route.params.id;
    this.isEditPage = this.$route.params.id ? true : false;
    this.getCourseById();
    this.getChapterById();
    this.getObjectiveById();
    this.getAllMaterials();
    this.getQuizByTeacher();

    useQuizQuestionsDragNDropStore.setIsAdaptive(this.isAdaptive);
    useQuizQuestionsStore.setIsAdaptive(this.isAdaptive);
    useQuizQuestionsGapStore.setIsAdaptive(this.isAdaptive);
  },
  updated() {
    this.active = Number(this.$route.params.tab) ?? 0;
    this.objectiveId = this.$route.params.id;
    this.allowSteps(this.$route.params.id);
  },
  watch: {
    $route() {
      removeItemLocalStorage("objectiveId");
    },
  },
  provide() {
    return {
      updateQuestionState: computed(() => this.updateDeletedQuestion),
      updateQuizProvide: computed(() => this.updateStatusQuiz),
    };
  },
  unmounted() {
    MaterialsService.setMaterialObjective([]);
  },
  beforeMount() {
    useQuizQuestionsStore.$reset();
    useQuizQuestionsDragNDropStore.$reset();
    useQuizQuestionsGapStore.$reset();
  },
  methods: {
    goBack(): void {
      const courseId = this.$route.params.courseId;
      this.$router.push({ name: "chapters", params: { id: courseId } });
    },
    getCourseById(): void {
      const courseId: any = this.$route.params.courseId;
      const chapterId: any = this.$route.params.chapterId;
      this.serviceCourse.course.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.course = response;

          setItemLocalStorage("objective", JSON.stringify(response));
          this.home.label = response.name;
        },
      });
      this.serviceCourse.getCoursesById(courseId);
    },
    getChapterById(): void {
      const courseId: any = this.$route.params.courseId;
      const chapterId: any = this.$route.params.chapterId;
      this.serviceCourse.chapter.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.chapter = response;
        },
      });
      this.serviceCourse.getChapterById(courseId, chapterId);
    },
    getObjectiveById(): void {
      const chapterId: any = this.$route.params.chapterId;
      const objectiveId: any = this.$route.params.id ?? this.objectiveId;
      if (objectiveId) {
        this.service.objective.subscribe({
          next: (response) => {
            if (response.createdAt !== "") {
              this.buttons.filter((e) => e.active === 0)[0].done = true;
            }
            this.objective = response;
            InfoObjectiveService.setInfoObjective(response);
          },
        });
        this.service.getObjectiveById(chapterId, objectiveId);
      }
    },
    changeTab(index: number): void {
      this.active = index;
      const params = {
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        id: this.$route.params.id,
        tab: index,
      };
      this.go.redirectToPage("formObjective", params);
    },
    allowSteps(active: any): void {
      this.buttons.forEach((e) => {
        if (e.active === active) {
          e.done = true;
          e.off = false;
        }
      });
    },
    setMaterialsPage(queryParams: QueryParams): void {
      const objectiveId: any = this.$route.params.id;
      if (objectiveId) {
        this.service
          .getMaterialsByObjective(objectiveId, queryParams)
          .then((response) => {
            const btn = this.buttons.filter((e) => e.active === 1)[0];
            btn.info = "";
            if (response) {
              btn.done = true;
              btn.off = false;
              this.params.total = response.total;
              this.params.size = response.size;
              MaterialsService.setMaterialObjective(response.content);
              this.blockScroll = false;
              this.getLessonPlans();
            } else {
              this.blockScroll = true;
              btn.off = this.objectiveId ? false : true;
              btn.info = this.$t("labels.generic", {
                pt: "Nenhum material cadastrado",
                en: "No materials registered",
              });
              this.loading = false;
            }
          })
          .finally(() => {
            this.loading = false;
          });
      }
    },
    scrollMaterials(event: HTMLDivElement): void {
      const { scrollTop, clientHeight, scrollHeight } = event;
      if (scrollTop + clientHeight >= scrollHeight) {
        if (!this.blockScroll) {
          this.params.page++;
          this.getAllMaterials();
        }
      }
    },
    getAllMaterials(): void {
      const page = this.params.page;
      const size = this.params.size;
      const groupType = this.params.groupType ?? undefined;
      const name = this.params.name ?? undefined;
      const params = new QueryParams(page, size);
      params.groupType = groupType;
      params.name = name;
      this.setMaterialsPage(params);
    },
    isMaterialDeleted(event: any): void {
      if (event === 0) {
        this.buttons[1].done = false;
      } else {
        this.buttons[1].done = true;
      }
      this.getLessonPlans();
    },
    onFilter(event: any): void {
      this.params.groupType = event;
      this.params.page = 0;
      this.loading = true;
      setTimeout(() => {
        this.getAllMaterials();
      }, 150);
    },
    onSearch(event: any): void {
      this.params.name = event;
      this.params.page = 0;
      this.loading = true;
      setTimeout(() => {
        this.getAllMaterials();
      }, 150);
    },
    getLessonPlans(): void {
      this.serviceLessonPlan.lessonPlans
        .pipe(take(2), last(), debounceTime(200))
        .subscribe({
          next: (response) => {
            const btn = this.buttons.filter((e) => e.active === 2)[0];
            if (
              response[0].materials?.length > 0 &&
              response[1].materials?.length > 0
            ) {
              btn.done = true;
              btn.off = false;
            } else {
              btn.done = false;
              btn.off = this.objectiveId ? false : true;
              if (!btn.info) {
                btn.info = this.$t("labels.generic", {
                  pt: "Planos sem material adicionado",
                  en: "Nenhum material cadastrado",
                });
              }
            }
            this.lessonPlans = response;
          },
        });
      this.serviceLessonPlan.getLessonPlan(this.objectiveId);
    },
    getQuizByTeacher(): void {
      const objectiveId: any = this.$route.params.id;
      if (objectiveId) {
        this.service.quiz.pipe(take(2), last(), debounceTime(200)).subscribe({
          next: (response) => {
            if (
              this.course?.type !== CourseType.ADAPTATIVE &&
              response == null
            ) {
              const btn = this.buttons.filter((e) => e.active === 3)[0];
              btn.done = true;
              btn.off = false;
              btn.info = "";
            } else if (
              this.course?.type !== CourseType.ADAPTATIVE ||
              response?.id
            ) {
              this.quizId = response.id;
              this.quiz = response;
              this.getQuestionsDefaultByQuiz();
              this.getQuestionByQuiz();
            } else {
              const btn = this.buttons.filter((e) => e.active === 3)[0];
              btn.done = false;
              btn.off = this.objectiveId ? false : true;
              btn.info = this.$t("labels.generic", {
                pt: "Quiz não foi cadastrado",
                en: "The quiz has not been registered",
              });
            }
          },
        });
        this.service.getQuizByTeacher(objectiveId);
      }
    },
    getQuestionsDefaultByQuiz(): void {
      this.service.questionsDefault.subscribe({
        next: (response) => {
          if (response?.content) this.questions.push(...response?.content);
          this.setQuizStatusButton();
        },
      });
      this.service.getQuestionsDefaultByQuiz(
        this.quizId,
        new QueryParams(0, 100)
      );
    },
    getQuestionByQuiz(): void {
      this.service.questions.pipe(take(2), last()).subscribe({
        next: (response) => {
          if (!response?.content) return;
          restoreAnswerTextItem(response.content);

          this.questions.push(...response?.content);
          this.setQuizStatusButton();
        },
      });
      this.service.getQuestionsByQuiz(this.quizId, new QueryParams(0, 100));
    },
    setQuizStatusButton() {
      if (
        this.course?.type !== CourseType.ADAPTATIVE ||
        this.questions.length >= this.quiz.numberQuestions
      ) {
        const btn = this.buttons.filter((e) => e.active === 3)[0];
        btn.done = true;
        btn.off = false;
      } else {
        const btn = this.buttons.filter((e) => e.active === 3)[0];
        btn.done = false;
        btn.off = this.objectiveId ? false : true;
        btn.info = this.$t("labels.generic", {
          pt: "Número de questões cadastradas é menor que o número de questões exibidas",
          en: "The number of registered questions is less than the number of displayed questions.",
        });
      }
    },
    updateStatusQuiz(value) {
      this.quiz = value;
      this.setQuizStatusButton();
    },
    updateDeletedQuestion(value: Question[]) {
      this.questions = value;
    },
  },
});
</script>
